'use client';

import { useState } from 'react';
import { formatDate } from '@/lib/utils';
import { LikeButton } from '@/components/like-button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import Image from 'next/image';
import Link from 'next/link';

interface Article {
  id: string;
  title: string;
  content: string;
  excerpt?: string;
  cover_image?: string;
  category?: string;
  tags?: string[];
  likes: number;
  views: number;
  created_at: string;
  updated_at: string;
}

interface ArticleCardProps {
  article: Article;
}

export function ArticleCard({ article }: ArticleCardProps) {
  return (
    <Link href={`/news/${article.id}`}>
      <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer overflow-hidden">
        {article.cover_image && (
          <div className="relative h-48 w-full overflow-hidden">
            <Image
              src={article.cover_image}
              alt={article.title}
              fill
              className="object-cover"
            />
          </div>
        )}
        <CardContent className="p-4">
          {article.category && (
            <Badge variant="secondary" className="mb-2">
              {article.category}
            </Badge>
          )}
          <h3 className="font-semibold line-clamp-2 mb-2 hover:text-primary transition-colors">
            {article.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
            {article.excerpt || article.content.replace(/<[^>]*>/g, '').slice(0, 100)}
          </p>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{formatDate(article.created_at)}</span>
            <div className="flex items-center gap-4">
              <span>{article.views} 阅读</span>
              <LikeButton articleId={article.id} initialLikes={article.likes} />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export function ArticleList({ articles }: { articles: Article[] }) {
  if (articles.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        暂无文章
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {articles.map((article) => (
        <ArticleCard key={article.id} article={article} />
      ))}
    </div>
  );
}
